/**
 * `/admin/referrals` - spec 11's reports (FR-6.3.4–FR-6.3.6).
 *
 * Two rules travel with this metric and are enforced by serving, not by the
 * client remembering:
 *
 * 1. **The words are the server's.** Every response carries `metric_label`
 *    ("Referrals initiated") and `metric_note` ("Counts taps, not confirmed
 *    purchases."). The panel renders those strings and never paraphrases them.
 * 2. **FR-6.3.3 forbids "usage", "orders" and "redemptions"** for this metric in
 *    any UI or export. There is no field in these payloads named or labelled with
 *    those words, and nothing in the panel adds one.
 *
 * These measure INTENT to contact - a tap on Call, Email, Website or Map - so
 * nothing here is a sale, and no screen may imply one.
 *
 * Ranges are over `event_date`, the Nepal calendar day (NFR-10.5.3), not the UTC
 * instant. A reversed range is refused (422 `referral_range_invalid`) rather than
 * swapped, because an empty report and a reversed one look identical.
 */

import { downloadFile, request } from "./client";
import type { WithNotice } from "./types";

export type ReferralBreakdown = {
  call: number;
  email: number;
  website: number;
  map: number;
};

export type ReferralTotals = {
  referrals_initiated: number;
  distinct_members: number;
  breakdown: ReferralBreakdown;
};

export type ReferralTrendPoint = {
  event_date: string;
  referrals_initiated: number;
};

export type VendorReferralRow = {
  vendor_id: string;
  name: string;
  /** False for a soft-deleted or unpublished partner, which keeps its history. */
  is_listed: boolean;
  referrals_initiated: number;
  distinct_members: number;
  breakdown: ReferralBreakdown;
  last_referral_at: string | null;
};

export type FamilyBizReferralRow = {
  family_business_id: string;
  name: string;
  is_listed: boolean;
  referrals_initiated: number;
  distinct_members: number;
  breakdown: ReferralBreakdown;
  last_referral_at: string | null;
};

export type LeaderboardEntry = {
  rank: number;
  target_kind: "vendor" | "family_biz";
  target_id: string;
  name: string;
  is_listed: boolean;
  referrals_initiated: number;
  distinct_members: number;
};

export type Leaderboard = {
  metric_label: string;
  metric_note: string;
  from_date: string | null;
  to_date: string | null;
  target_kind: "vendor" | "family_biz";
  items: LeaderboardEntry[];
  total: number;
} & WithNotice;

export type VendorReferralReport = {
  metric_label: string;
  metric_note: string;
  from_date: string | null;
  to_date: string | null;
  totals: ReferralTotals;
  items: VendorReferralRow[];
  total: number;
  trend: ReferralTrendPoint[];
} & WithNotice;

export type FamilyBizReferralReport = {
  metric_label: string;
  metric_note: string;
  from_date: string | null;
  to_date: string | null;
  totals: ReferralTotals;
  items: FamilyBizReferralRow[];
  total: number;
  trend: ReferralTrendPoint[];
} & WithNotice;

export type ReportRange = {
  from_date?: string;
  to_date?: string;
};

export function fetchVendorReport(
  range: ReportRange,
  extra: { vendor_id?: string; limit?: number; offset?: number } = {},
): Promise<VendorReferralReport> {
  return request<VendorReferralReport>("/admin/referrals/vendors", {
    query: { ...range, ...extra },
  });
}

export function fetchFamilyBizReport(
  range: ReportRange,
  extra: { listing_id?: string; limit?: number; offset?: number } = {},
): Promise<FamilyBizReferralReport> {
  return request<FamilyBizReferralReport>("/admin/referrals/family-biz", {
    query: { ...range, ...extra },
  });
}

export function fetchLeaderboard(
  targetKind: "vendor" | "family_biz",
  range: ReportRange,
  limit = 10,
): Promise<Leaderboard> {
  return request<Leaderboard>("/admin/referrals/leaderboard", {
    query: { target_kind: targetKind, ...range, limit },
  });
}

/**
 * FR-6.3.6 - the CSV.
 *
 * Downloaded as a blob rather than linked to, because the endpoint is admin-only
 * and `window.open` cannot carry the `Authorization` header. The filename comes
 * from the server's `Content-Disposition`, and the file's first line is the
 * "taps, not confirmed purchases" caveat as a `#`-prefixed comment, so it still
 * parses as one CSV with a single header row.
 *
 * An export is not a page: this is deliberately unpaginated.
 */
export function downloadReferralCsv(
  targetKind: "vendor" | "family_biz" | null,
  range: ReportRange,
): Promise<void> {
  return downloadFile("/admin/referrals/export.csv", {
    query: { target_kind: targetKind ?? undefined, ...range },
    // A large export over a slow link gets longer than the default.
    timeoutMs: 60000,
  });
}
